const { domReady } = wp;
import { store } from '@wordpress/interactivity';
import type JQuery from 'jquery'; // from @types/jquery

domReady(() => {
	const addToCartForm = document.querySelector(
		'div.wc-block-add-to-cart-form'
	);
	if (addToCartForm) {
		mutateStockIndicator(addToCartForm);
	}
});

type ServerState = {
	state: {
		productId: number;
		variationId: number | null;
		sku: string | null;
		productType: 'simple' | 'grouped' | 'external' | 'variable';
		defaultPrice: string; // The default display price for the current product or selected variation before any of our custom discounts are applied
		discountedPrice: string | null; // null if no percentage discounts or the product of defaultPrice and all percentage discounts. Null value allows guard clauses.
		displayPrice: string; // discountedPrice or defaultPrice if null
		variationPriceNode: Element | null; // the variation price in variation_single container
		bulkDiscount: {
			isActive: boolean;
			percentageDiscount: number | null;
			ranges: { percentage_discount: number; range: number[] }[] | null;
		};
		cuaDiscount: {
			percentageDiscount: number | null;
		};
	};
};

type Variation = {
	sku: string;
	variation_id: number;
	display_price: number;
	display_regular_price: number;
};

const variationContainer = document.querySelector('div.woocommerce-variation');

const storeDef = {
	state: {
		get percentageDiscount(): number {
			const discounts = [
				state.bulkDiscount.percentageDiscount,
				state.cuaDiscount.percentageDiscount,
			];
			const total = discounts.reduce<number>((sum, val) => {
				if (!val) {
					return sum;
				}
				return sum + val;
			}, 0);
			return total;
		},
		get discountedPrice(): string | null {
			if (state.percentageDiscount <= 0) {
				return null;
			}
			const productPrice = parseFloat(state.defaultPrice);
			const percentageOfOriginalPrice =
				1 - state.percentageDiscount / 100;

			const discountedPrice = productPrice * percentageOfOriginalPrice;
			return discountedPrice.toFixed(2);
		},
		get displayPrice(): string {
			return state.discountedPrice
				? state.discountedPrice
				: state.defaultPrice;
		},
	},
	actions: {
		init: () => {
			actions.bulkDiscount.init();

			const resetVariationButton = document.querySelector(
				'a.reset_variations'
			) as HTMLAnchorElement | null;
			if (resetVariationButton) {
				resetVariationButton.addEventListener('click', () => {
					state.variationId = null;
					state.sku = null;
				});
			}

			if (state.productType === 'variable') {
				jQuery('form.variations_form').on(
					'show_variation',
					(event: JQuery.Event, variation: Variation) => {
						showVariation(event, variation);
					}
				);
			}
		},
		watchDiscountPrice: () => {
			if (!state.discountedPrice) {
				return;
			}
			if (state.variationPriceNode) {
				mutateVariationPrice(
					state.variationPriceNode,
					state.displayPrice
				);
			}
		},
		bulkDiscount: {
			init: () => {
				const bulkDiscountRanges = state.bulkDiscount.ranges; // asignment to constant for the guard clause to correctly narrow the type.
				if (!bulkDiscountRanges) {
					return;
				}
				const inputElement = document.querySelector(
					'.input-text.qty.text'
				) as HTMLInputElement | null;

				inputElement?.addEventListener('change', () => {
					const quantity = Number(inputElement.value);
					state.bulkDiscount.percentageDiscount =
						findPercentageDiscount(quantity, bulkDiscountRanges);
				});
			},
		},
	},
	callbacks: {},
};

/**
 * Triggered by the WooCommerce show_variation event when a variation is selected.
 * @see https://rudrastyh.com/woocommerce/trigger-event-after-change-of-variation.html
 *
 * @param event     jQuery event
 * @param variation product variation
 */
function showVariation(event: JQuery.Event, variation: Variation) {
	state.defaultPrice = variation.display_price.toString();
	state.variationId = variation.variation_id;
	state.sku = variation.sku;

	if (!variationContainer) {
		return;
	}
	state.variationPriceNode = getVariationPriceNode(
		variationContainer,
		variation
	);
	buildVariationSku(variationContainer, state.sku);
	mutateStockIndicator(variationContainer);

	if (state.variationPriceNode && state.discountedPrice) {
		mutateVariationPrice(state.variationPriceNode, state.displayPrice);
	}
}

/**
 * Get the current variation price node
 *
 * @param container container
 * @param variation product variation
 * @return variation price node
 */
function getVariationPriceNode(
	container: Element,
	variation: Variation
): ServerState['state']['variationPriceNode'] {
	const isOnSale =
		variation.display_price !== variation.display_regular_price;

	let priceNode = null;
	if (isOnSale) {
		priceNode = container.querySelector('ins span.amount bdi');
	} else {
		priceNode = container.querySelector('span.amount bdi');
	}

	return priceNode;
}

/**
 * Adds a SKU element to the single variation container.
 *
 * @param container container
 * @param sku       sku variation sku
 */
function buildVariationSku(container: Element, sku: string | null) {
	if (!sku) {
		return;
	}

	const variationSku = container.querySelector('div.variation-sku');
	if (variationSku) {
		if (variationSku.lastChild) {
			variationSku.lastChild.textContent = sku;
		}
	} else {
		const variationSkuNodeContainer = document.createElement('div');
		variationSkuNodeContainer.classList.add('variation-sku');
		variationSkuNodeContainer.innerText = 'SKU:';

		const variationSkuNodeText = document.createElement('span');
		variationSkuNodeText.innerText = sku;

		variationSkuNodeContainer.appendChild(variationSkuNodeText);
		container.appendChild(variationSkuNodeContainer);
	}
}

/**
 * Changes the variation price to displayPrice.
 *
 * @param priceNode    variation price node
 * @param displayPrice display price
 */
function mutateVariationPrice(priceNode: Element, displayPrice: string) {
	if (!priceNode?.lastChild) {
		return;
	}
	priceNode.lastChild.textContent = displayPrice;
}

/**
 * Adds 'not-low-stock' class to the variation stock indicator for variations that are 'in-stock' to differentiate their styling with a green badge.
 * This is necessary because WooCommerce doesn't differentiate between high stock and low stock in this element, only in-stock and out-of-stock.
 *
 * @param container variation container
 */
function mutateStockIndicator(container: Element) {
	const backorderIndicator = container.querySelector('p.stock');
	const inStockIndicator = container.querySelector('p.in-stock');
	const outOfStockIndicator = container.querySelector('p.out-of-stock');

	const stock = backorderIndicator || inStockIndicator || outOfStockIndicator;

	if (!stock) {
		return;
	}

	if (stock.textContent.includes('Only')) {
		stock.classList.remove('in-stock');
		stock.classList.add('low-stock');
	}

	// Remove skeleton state
	stock.classList.add('js-loaded');
}

function findPercentageDiscount(
	quantity: number,
	discountRanges: NonNullable<ServerState['state']['bulkDiscount']['ranges']>
): number | null {
	const highestRangeObj = discountRanges[discountRanges.length - 1];
	const highestRangeQuantity =
		highestRangeObj.range[highestRangeObj.range.length - 1];

	if (quantity > highestRangeQuantity) {
		// If the input value is greater than the highest end range value, still use that percentage discount
		return highestRangeObj.percentage_discount;
	}

	const matchingRangeGroup = discountRanges.find((rangeGroup) =>
		rangeGroup.range.includes(quantity)
	);

	return matchingRangeGroup ? matchingRangeGroup.percentage_discount : null;
}

type Store = ServerState & typeof storeDef;

const { state, actions } = store<Store>('product-price-updates', storeDef);
